// Utility functions for cascading dropdown data handling

/**
 * USAGE INSTRUCTIONS:
 *
 * 1. In your form submission handler, import and use consolidateCascadingDropdowns:
 *
 *    import { consolidateCascadingDropdowns } from './CascadingDropdownUtils';
 *
 *    const handleSubmit = () => {
 *      const consolidatedData = consolidateCascadingDropdowns(formData, moduleSections);
 *      // Send consolidatedData to backend
 *    };
 *
 * 2. The function will convert this structure:
 *    {
 *      "fieldName": "parent_value",
 *      "fieldName_child_0": "child_level_0_value",
 *      "fieldName_child_1": "child_level_1_value"
 *    }
 *
 *    Into this structure:
 *    {
 *      "fieldName": {
 *        "parent": "parent_value",
 *        "children": [
 *          { "level": 0, "value": "child_level_0_value" },
 *          { "level": 1, "value": "child_level_1_value" }
 *        ]
 *      }
 *    }
 */

/**
 * Consolidates cascading dropdown data before form submission
 * Converts separate child fields (field_child_0, field_child_1) into a single hierarchical structure
 * @param {Object} formData - The form data object containing field values
 * @param {Array} sections - Module sections with field definitions
 * @returns {Object} - Consolidated form data with hierarchical dropdown structure
 */
export const consolidateCascadingDropdowns = (formData, sections) => {
    if (!sections) return formData;

    const consolidatedData = { ...formData };

    sections.forEach(section => {
        if (!section.fields) return;

        section.fields.forEach(field => {
            // Check if this is a select field with cascading children
            if (field.type === 'select' && field.dropDownId?.dropDownItems?.some(item => item.children && item.children.length > 0)) {
                const fieldValue = consolidatedData[field.name];
                if (fieldValue) {
                    let parentValue;
                    let existingChildren = [];

                    // Check if the field value is already consolidated
                    if (typeof fieldValue === 'object' && fieldValue.parent && fieldValue.children) {
                        // Already consolidated - extract the actual parent value and existing children
                        parentValue = fieldValue.parent;
                        existingChildren = fieldValue.children || [];
                        console.log(`Field ${field.name} is already consolidated, parent: ${parentValue}, existing children:`, existingChildren);
                    } else {
                        // Simple string value
                        parentValue = fieldValue;
                        console.log(`Field ${field.name} is a simple value: ${parentValue}`);
                    }

                    const children = [];

                    // Collect all child values for this field from the form data
                    Object.keys(consolidatedData).forEach(key => {
                        if (key.startsWith(field.name + '_child_')) {
                            const level = parseInt(key.split('_child_')[1]);
                            const childValue = consolidatedData[key];
                            if (childValue) {
                                children.push({ level, value: childValue });
                                // Remove individual child fields from form data
                                delete consolidatedData[key];
                            }
                        }
                    });

                    // Sort children by level
                    children.sort((a, b) => a.level - b.level);

                    // Create consolidated structure only if there are children or we had existing children
                    if (children.length > 0) {
                        consolidatedData[field.name] = {
                            parent: parentValue,
                            children: children
                        };
                        console.log(`Consolidated ${field.name} with ${children.length} children:`, consolidatedData[field.name]);
                    } else if (existingChildren.length > 0) {
                        // No new children from form, but we had existing children - preserve the consolidated structure
                        consolidatedData[field.name] = {
                            parent: parentValue,
                            children: existingChildren
                        };
                        console.log(`Preserved existing consolidated structure for ${field.name}:`, consolidatedData[field.name]);
                    } else {
                        // No children at all - keep as simple value
                        consolidatedData[field.name] = parentValue;
                        console.log(`No children for ${field.name}, keeping as simple value: ${parentValue}`);
                    }
                }
            }
        });
    });

    return consolidatedData;
};

/**
 * Extracts cascading dropdown data when loading existing records
 * Converts hierarchical structure back to separate fields for UI management
 * @param {Object} fieldValues - Current field values
 * @param {Array} sections - Module sections with field definitions
 * @returns {Object} - Object containing extracted nested values
 */
export const extractCascadingDropdowns = (fieldValues, sections) => {
    if (!sections || !fieldValues) return {};

    const nestedValues = {};

    sections.forEach(section => {
        if (!section.fields) return;

        section.fields.forEach(field => {
            if (field.type === 'select' && fieldValues[field.name]) {
                const fieldValue = fieldValues[field.name];

                // Check if this is a consolidated cascading dropdown value
                if (typeof fieldValue === 'object' && fieldValue.parent && fieldValue.children) {
                    // Extract children values into nested state
                    fieldValue.children.forEach(child => {
                        const childFieldName = `${field.name}_child_${child.level}`;
                        nestedValues[childFieldName] = child.value;
                    });
                }
            }
        });
    });

    return nestedValues;
};